import { useFocusedBranchProvider } from '@client/page/management/table-group/table-group-list/component/branch-selection/focused-branch-context';
import { useDialog } from 'ui/src/modal/use-dialog';
import { useNotificationCenter } from 'ui/src/notification/notification-center';
import { useLocalStorage } from 'usehooks-ts';
import { OnlineOrderBranchSettingFragment } from '@client/graphql/online-order-branch-setting-fragment';
import { getFragmentData } from '@client/graphql/types';
import { useCallback, useEffect } from 'react';
import ToggleInput from 'ui/src/form/input/toggle-input';
import { useTranslate } from '@tolgee/react';
import useGetOnlineOrderBranchConfiguration from '../logic/use-online-order-branch-config';
import useUpdateOnlineOrderConfiguration from '../../setting/logic/use-update-online-order-branch-setting';

export default function OnlineOrderHeaderDeliveryToggle() {
  const branchProvider = useFocusedBranchProvider();
  const branchID = branchProvider.branch?.id ?? '';
  const { showAlert } = useNotificationCenter();
  const { data: settingRawData } = useGetOnlineOrderBranchConfiguration(branchID);
  const [update] = useUpdateOnlineOrderConfiguration();
  const dialog = useDialog();
  const [deliveryEnabled, setDeliveryEnabled] = useLocalStorage<boolean>('isDeliveryEnabled', false);
  const [deliveryPause, setDeliveryPause] = useLocalStorage<boolean>('isDeliveryPause', false);

  const onlineOrderBranchSettingData = getFragmentData(
    OnlineOrderBranchSettingFragment,
    settingRawData?.onlineOrderBranchConfiguration,
  );

  const { t } = useTranslate();

  useEffect(() => {
    if (onlineOrderBranchSettingData) {
      setDeliveryPause(onlineOrderBranchSettingData.deliveryPause);
      setDeliveryEnabled(!onlineOrderBranchSettingData.deliveryEnable);
    } else {
      setDeliveryPause(false);
      setDeliveryEnabled(false);
    }
  }, [onlineOrderBranchSettingData, setDeliveryEnabled, setDeliveryPause]);

  const handleEnableAction = useCallback(
    (value: boolean) => {
      dialog.confirmAlert({
        title: t('online-order.dashboard.delivery-toggle.alert.title'),
        message: t('online-order.dashboard.delivery-toggle.alert.message'),
        textButton: t('common.button.confirm'),
        onConfirm: () =>
          update({
            variables: {
              branchID: branchID,
              input: {
                deliveryEnable: !value,
              },
            },
          })
            .then(() => {
              setDeliveryEnabled(value);
              showAlert({
                status: 'success',
                title: t('alert.title.success'),
                message: t('online-order.dashboard.alert.message.success'),
              });
            })
            .catch(() => {
              showAlert({
                status: 'error',
                title: t('alert.title.success'),
                message: t('online-order.dashboard.alert.message.error'),
              });
            }),
      });
    },
    [branchID, dialog, setDeliveryEnabled, showAlert, update, t],
  );

  const handlePauseAction = useCallback(
    (value: boolean) => {
      dialog.confirmAlert({
        title: t('online-order.dashboard.delivery-toggle.alert.title'),
        message: t('online-order.dashboard.delivery-toggle.alert.message'),
        textButton: t('common.button.confirm'),
        onConfirm: () =>
          update({
            variables: {
              branchID: branchID,
              input: {
                deliveryPause: value,
              },
            },
          })
            .then(() => {
              setDeliveryEnabled(value);
              showAlert({
                status: 'success',
                title: t('alert.title.success'),
                message: t('online-order.dashboard.alert.message.success'),
              });
            })
            .catch(() => {
              showAlert({
                status: 'error',
                title: t('alert.title.success'),
                message: t('online-order.dashboard.alert.message.error'),
              });
            }),
      });
    },
    [branchID, dialog, setDeliveryEnabled, showAlert, update, t],
  );

  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex items-center justify-center gap-2 px-2 py-1 border border-1 border-gray-300 rounded-md scale-95">
        <p className="font-semibold text-sm">{t('online-order.text.delivery')}</p>
        <ToggleInput
          value={deliveryEnabled}
          onChange={(newVal) => {
            handleEnableAction(newVal);
          }}
        />
      </div>

      <div className="flex items-center justify-center gap-2 px-2 py-1 border border-1 border-gray-300 rounded-md scale-95">
        <p className="font-semibold text-sm">{t('online-order.text.delivery-pause')}</p>
        <ToggleInput
          value={deliveryPause}
          onChange={(newVal) => {
            handlePauseAction(newVal);
          }}
        />
      </div>
    </div>
  );
}
